import React, { useState, useEffect, useRef } from "react";
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  Image,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
} from "react-native";
import { Toast } from "react-native-toast-notifications";
import { router, useLocalSearchParams } from "expo-router";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import { appActions } from "../../store/slices/appSlice";
import customAxios from "../../utils/customAxios";
import i18n from "../../locales/i18n";
import WebSidebar from "./WebSidebar";
import { useStock } from "../../hooks/useStock";

// Import the stock image for header
import StockHeaderImage from "../../assets/images/stock.png";
import ScanAreaIcon from "../../assets/web/add-product/scan-area.png";

const WebAddProductScreen: React.FC = () => {
  const dispatch = useDispatch();
  const { locale } = useSelector((state: RootState) => state.app);

  // Check if current language is RTL (Arabic)
  const isRTL = locale === "ar" || i18n.locale === "ar";

  // Get route parameters for edit mode
  const params = useLocalSearchParams();
  const isEditMode = params.mode === "edit";

  // Extract params with proper string handling
  const paramProductName = Array.isArray(params.productName)
    ? params.productName[0]
    : params.productName || "";
  const paramPriceSell = Array.isArray(params.priceSell)
    ? params.priceSell[0]
    : params.priceSell || "";
  const paramPriceBought = Array.isArray(params.priceBought)
    ? params.priceBought[0]
    : params.priceBought || "";
  const paramBarcode = Array.isArray(params.barcode)
    ? params.barcode[0]
    : params.barcode || "";
  const paramTypeOfQuantity = Array.isArray(params.typeOfQuantity)
    ? params.typeOfQuantity[0]
    : params.typeOfQuantity || "unit";
  const paramStock = Array.isArray(params.stock)
    ? params.stock[0]
    : params.stock || "";
  const paramPhoto = Array.isArray(params.photo)
    ? params.photo[0]
    : params.photo || "";
  const paramProductId = Array.isArray(params.productId)
    ? params.productId[0]
    : params.productId || "";

  // Form state - pre-fill with edit mode data if available
  const [productName, setProductName] = useState(
    isEditMode ? paramProductName : ""
  );
  const [barcode, setBarcode] = useState(isEditMode ? paramBarcode : "");
  const [buyPrice, setBuyPrice] = useState(isEditMode ? paramPriceBought : "");
  const [sellPrice, setSellPrice] = useState(isEditMode ? paramPriceSell : "");
  const [quantity, setQuantity] = useState(isEditMode ? paramStock : "");
  const [unitType, setUnitType] = useState(
    isEditMode ? paramTypeOfQuantity : "unit"
  );
  const [photo, setPhoto] = useState<string | null>(
    isEditMode && paramPhoto ? paramPhoto : null
  );
  const [photoFile, setPhotoFile] = useState<File | null>(null); // Store actual file for upload
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showUnitDropdown, setShowUnitDropdown] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { deleteProduct } = useStock();

  const unitTypes = [
    { label: i18n.t("unit"), value: "unit" },
    { label: i18n.t("kilogram"), value: "kilogram" },
    { label: i18n.t("liter"), value: "liter" },
  ];

  // Barcode scanner detection refs and state
  const barcodeInputRef = useRef<any>(null);
  const lastKeyTime = useRef<number>(0);
  const barcodeBuffer = useRef<string>("");
  const scannerTimeoutRef = useRef<any>(null);
  const [isScannerActive, setIsScannerActive] = useState(false);

  // Scanner detection: Scanners type very fast and end with Enter
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const currentTime = Date.now();
      const timeDiff = currentTime - lastKeyTime.current;

      // If Enter is pressed and we have buffered input
      if (event.key === "Enter" && barcodeBuffer.current.length > 0) {
        // This was likely a scanner input
        setBarcode(barcodeBuffer.current);
        barcodeBuffer.current = "";
        setIsScannerActive(false);
        Toast.show(i18n.t("barcodeScanned"), {
          type: "success",
        });
        event.preventDefault();
        return;
      }

      // Check if this is fast typing (scanner typically < 50ms between keys)
      if (timeDiff < 50 && /^[0-9]$/.test(event.key)) {
        // This looks like scanner input
        setIsScannerActive(true);
        barcodeBuffer.current += event.key;

        // Clear any existing timeout
        if (scannerTimeoutRef.current) {
          clearTimeout(scannerTimeoutRef.current);
        }

        // Set timeout to finalize scanner input if no Enter is received
        scannerTimeoutRef.current = setTimeout(() => {
          if (barcodeBuffer.current.length >= 6) {
            setBarcode(barcodeBuffer.current);
            Toast.show(i18n.t("barcodeScanned"), {
              type: "success",
            });
          }
          barcodeBuffer.current = "";
          setIsScannerActive(false);
        }, 100);
      } else if (/^[0-9]$/.test(event.key)) {
        // Normal typing, reset buffer
        barcodeBuffer.current = event.key;
      }

      lastKeyTime.current = currentTime;
    };

    if (typeof window !== "undefined") {
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("keydown", handleKeyDown);
      }
      if (scannerTimeoutRef.current) {
        clearTimeout(scannerTimeoutRef.current);
      }
    };
  }, []);

  const handleScanButtonClick = () => {
    // Focus the barcode input
    barcodeInputRef.current?.focus();
    Toast.show(i18n.t("scannerReady"), {
      type: "info",
    });
  };

  const handleSubmit = async () => {
    // Validation
    if (!productName.trim()) {
      Toast.show(i18n.t("fillAllFields"), {
        type: "danger",
      });
      return;
    }

    if (!buyPrice || !sellPrice) {
      Toast.show(i18n.t("fillAllFields"), {
        type: "danger",
      });
      return;
    }

    if (parseFloat(sellPrice) < parseFloat(buyPrice)) {
      Toast.show(
        i18n.t("priceValidation"),
        { type: "danger" }
      );
      return;
    }

    setIsSubmitting(true);

    try {
      // Create FormData like mobile does
      const formData = new FormData();
      formData.append("name", productName.trim());
      if (barcode.trim()) formData.append("barcode", barcode.trim());
      formData.append("price_bought", buyPrice);
      formData.append("price_sell", sellPrice);
      formData.append("stock", quantity || "0");
      formData.append("typeOfQuantity", unitType || "unit");

      // Handle photo for web - use actual file object
      if (photoFile) {
        // Append actual file for proper multipart upload
        formData.append("photo", photoFile, photoFile.name);
      }

      if (isEditMode && paramProductId) {
        // Edit mode - use PATCH endpoint
        await customAxios.patch(`/stock/${paramProductId}`, formData, {
          withCredentials: true,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        Toast.show(i18n.t("productUpdateSuccess"), {
          type: "success",
        });
      } else {
        // Add mode - use POST endpoint
        await customAxios.post("/stock/add", formData, {
          withCredentials: true,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        Toast.show(i18n.t("productAddedSuccess"), {
          type: "success",
        });
      }

      dispatch(appActions.toggleStatsRefresh());

      // Navigate back to stock page
      router.back();
    } catch (error: any) {
      if (error?.response?.status === 403) {
        const errorMessage = error?.response?.data?.error || "";
        if (
          typeof errorMessage === "string" &&
          errorMessage.toLowerCase().includes("free tier")
        ) {
          Toast.show(
            i18n.t("freeTierLimitExceeded") + " " + i18n.t("upgradeRequired"),
            {
              type: "danger",
              duration: 5000,
            }
          );
          setTimeout(() => {
            router.push("/(drawer)/params/limits");
          }, 2000);
          return;
        }
      }

      const errorMessage =
        error.response?.data?.message || error.message || i18n.t("addfaild");
      Toast.show(errorMessage, { type: "danger" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      key={`add-product-${locale}`}
      className="flex flex-1 bg-[#F5F5F5] min-h-screen flex-row"
    >
      <WebSidebar />

      <View style={styles.mainContent}>
        <ScrollView
          style={styles.scrollView}
          showsVerticalScrollIndicator={false}
        >
          {/* Header with blue bar */}
          <View style={styles.headerSection}>
            <div
              className={`flex flex-row items-center gap-4 ${isRTL ? "justify-end flex-row-reverse" : "justify-start"}`}
            >
              <View style={styles.headerIconContainer}>
                <Image
                  source={StockHeaderImage}
                  style={styles.headerIcon}
                  resizeMode="contain"
                />
              </View>
              <Text style={styles.headerTitle}>
                {i18n.t("stock")} {"  >  "}{" "}
                {isEditMode
                  ? i18n.t("editproduct")
                  : i18n.t("addproduct")}
              </Text>
            </div>
            <View style={styles.headerBar} />
          </View>

          {/* Form Content */}
          <div
            className={`flex flex-row gap-8 ${isRTL ? "" : "flex-row-reverse"}`}
            style={{ marginTop: 24 }}
          >
            {/* Image Column */}
            <View style={styles.imageColumn}>
              <Text style={styles.sectionLabel}>
                {i18n.t("image")}
              </Text>
              <TouchableOpacity
                style={styles.imageUploadArea}
                onPress={() => {
                  // Trigger file input click for web
                  const fileInput = document.createElement("input");
                  fileInput.type = "file";
                  fileInput.accept = "image/*";
                  fileInput.onchange = (e: any) => {
                    const file = e.target.files[0];
                    if (file) {
                      if (file.size > 600 * 1024) {
                        Toast.show(
                          i18n.t("imageSizeError"),
                          { type: "danger" }
                        );
                        return;
                      }
                      // Store the file for upload
                      setPhotoFile(file);
                      // Create preview URL
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        setPhoto(event.target?.result as string);
                      };
                      reader.readAsDataURL(file);
                    }
                  };
                  fileInput.click();
                }}
              >
                {photo ? (
                  <Image
                    source={{ uri: photo }}
                    style={styles.uploadedImage}
                    resizeMode="cover"
                  />
                ) : (
                  <View style={styles.imagePlaceholder}>
                    <Text style={styles.placeholderText}>
                      {i18n.t("addpicture")}
                    </Text>
                  </View>
                )}
              </TouchableOpacity>
            </View>

            {/* Form Fields Column */}
            <View style={styles.formColumn}>
              {/* Barcode with Scan Button */}
              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>
                  {i18n.t("barcode")}
                </Text>
                <View style={styles.barcodeInputRow}>
                  <TouchableOpacity
                    style={styles.scanButton}
                    onPress={handleScanButtonClick}
                  >
                    <Image
                      source={ScanAreaIcon}
                      style={styles.scanIcon}
                      resizeMode="contain"
                    />
                    <Text style={styles.scanButtonText}>
                      {i18n.t("scanBarcode")}
                    </Text>
                  </TouchableOpacity>
                  <TextInput
                    ref={barcodeInputRef}
                    style={[
                      styles.barcodeInput,
                      isScannerActive && styles.barcodeInputActive,
                    ]}
                    placeholder="6 345 345 43534 345"
                    value={barcode}
                    onChangeText={setBarcode}
                    placeholderTextColor="#999"
                  />
                </View>
              </View>

              {/* Product Name */}
              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>
                  {i18n.t("productname")}
                </Text>
                <TextInput
                  style={styles.input}
                  placeholder={i18n.t("productname")}
                  value={productName}
                  onChangeText={setProductName}
                  placeholderTextColor="#999"
                />
              </View>

              {/* Buy Price */}
              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>
                  {i18n.t("boughtprice")}
                </Text>
                <TextInput
                  style={styles.input}
                  placeholder={`0 ${i18n.t("currency")}`}
                  value={buyPrice}
                  onChangeText={setBuyPrice}
                  keyboardType="numeric"
                  placeholderTextColor="#999"
                />
              </View>

              {/* Sell Price */}
              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>
                  {i18n.t("sellprice")}
                </Text>
                <TextInput
                  style={styles.input}
                  placeholder={`0 ${i18n.t("currency")}`}
                  value={sellPrice}
                  onChangeText={setSellPrice}
                  keyboardType="numeric"
                  placeholderTextColor="#999"
                />
              </View>

              {/* Profit Display */}
              {buyPrice &&
                sellPrice &&
                parseFloat(sellPrice) >= parseFloat(buyPrice) && (
                  <View style={styles.profitDisplay}>
                    <Text style={styles.profitLabel}>
                      {i18n.t("profit")}:
                    </Text>
                    <Text style={styles.profitValue}>
                      {(parseFloat(sellPrice) - parseFloat(buyPrice)).toFixed(
                        2
                      )}{" "}
                      {i18n.t("currency")}
                    </Text>
                  </View>
                )}

              {/* Unit Type */}
              <View
                style={[
                  styles.inputGroup,
                  { position: "relative" as any, zIndex: 1000 },
                ]}
              >
                <Text style={styles.inputLabel}>
                  {i18n.t("typeOfQuantity")}
                </Text>
                <TouchableOpacity
                  style={styles.selectInput}
                  onPress={() => setShowUnitDropdown(!showUnitDropdown)}
                >
                  <Text style={styles.selectIcon}>▼</Text>
                  <Text style={styles.selectText}>
                    {unitTypes.find((u) => u.value === unitType)?.label ||
                      unitTypes[0]?.label}
                  </Text>
                </TouchableOpacity>
                {showUnitDropdown && (
                  <View style={styles.dropdownMenu}>
                    {unitTypes.map((unit) => (
                      <TouchableOpacity
                        key={unit.value}
                        style={[
                          styles.dropdownItem,
                          unitType === unit.value &&
                            styles.dropdownItemSelected,
                        ]}
                        onPress={() => {
                          setUnitType(unit.value);
                          setShowUnitDropdown(false);
                        }}
                      >
                        <Text
                          style={[
                            styles.dropdownItemText,
                            unitType === unit.value &&
                              styles.dropdownItemTextSelected,
                          ]}
                        >
                          {unit.label}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                )}
              </View>

              {/* Quantity */}
              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>
                  {i18n.t("quantity")}
                </Text>
                <TextInput
                  style={styles.input}
                  placeholder="0"
                  value={quantity}
                  onChangeText={setQuantity}
                  keyboardType="numeric"
                  placeholderTextColor="#999"
                />
              </View>
            </View>
          </div>

          {/* Submit Button */}
          <View style={styles.buttonContainer}>
            {isEditMode && (
              <TouchableOpacity
                style={styles.webDeleteButton}
                onPress={() => setShowDeleteModal(true)}
              >
                <Text style={styles.webDeleteButtonText}>
                  🗑️ {i18n.t("deleteProduct")}
                </Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity
              style={styles.cancelButton}
              onPress={() => router.back()}
            >
              <Text style={styles.cancelButtonText}>
                {i18n.t("cancel")}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.submitButton}
              onPress={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={styles.submitButtonText}>
                  {i18n.t("save")}
                </Text>
              )}
            </TouchableOpacity>
          </View>
        </ScrollView>
      </View>

      {/* Custom Delete Confirmation Modal */}
      {showDeleteModal && (
        <TouchableOpacity
          activeOpacity={1}
          onPress={() => setShowDeleteModal(false)}
          style={styles.webModalOverlay}
        >
          <TouchableOpacity
            activeOpacity={1}
            onPress={(e) => e.stopPropagation()}
            style={styles.webModalContent}
          >
            {/* Warning Icon */}
            <View style={styles.webModalIcon}>
              <Text style={{ fontSize: 36 }}>⚠️</Text>
            </View>
            <Text style={styles.webModalTitle}>{i18n.t("deleteProduct")}</Text>
            <Text style={styles.webModalMessage}>
              {i18n.t("confirmDelete")}
            </Text>
            <Text style={styles.webModalProductName}>
              {productName || paramProductName}
            </Text>
            <View style={styles.webModalActions}>
              <TouchableOpacity
                style={styles.webModalCancelBtn}
                onPress={() => setShowDeleteModal(false)}
                disabled={isDeleting}
              >
                <Text style={styles.webModalCancelText}>
                  {i18n.t("cancel")}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.webModalDeleteBtn,
                  isDeleting && { opacity: 0.6 },
                ]}
                onPress={async () => {
                  setIsDeleting(true);
                  const success = await deleteProduct(Number(paramProductId));
                  setIsDeleting(false);
                  setShowDeleteModal(false);
                  if (success) {
                    router.back();
                  }
                }}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <ActivityIndicator size="small" color="#FFF" />
                ) : (
                  <Text style={styles.webModalDeleteText}>
                    {i18n.t("delete")}
                  </Text>
                )}
              </TouchableOpacity>
            </View>
          </TouchableOpacity>
        </TouchableOpacity>
      )}
    </div>
  );
};

const styles = StyleSheet.create({
  mainContent: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
    padding: 24,
  },
  headerSection: {
    marginBottom: 24,
  },
  headerTitle: {
    fontSize: 28,
    fontWeight: "bold",
    color: "#333",
  },
  headerIconContainer: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: "#E8F4F8",
    borderWidth: 4,
    borderColor: "#1379A2",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  headerIcon: {
    width: 70,
    height: 70,
  },
  headerBar: {
    height: 8,
    backgroundColor: "#1379A2",
    borderRadius: 4,
    marginTop: 12,
  },
  formContainer: {
    flexDirection: "row",
    gap: 32,
    marginTop: 24,
  },
  imageColumn: {
    width: 280,
  },
  formColumn: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    padding: 24,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2,
  },
  leftColumn: {
    width: 280,
  },
  rightColumn: {
    flex: 1,
  },
  sectionLabel: {
    fontSize: 16,
    fontWeight: "600",
    color: "#333",
    marginBottom: 12,
    textAlign: "center",
  },
  imageUploadArea: {
    backgroundColor: "#F8FAFB",
    borderRadius: 12,
    borderWidth: 2,
    borderColor: "#1379A2",
    borderStyle: "dashed",
    padding: 20,
    alignItems: "center",
    justifyContent: "center",
    minHeight: 280,
  },
  uploadedImage: {
    width: "100%",
    height: 250,
    borderRadius: 8,
  },
  imagePlaceholder: {
    alignItems: "center",
    justifyContent: "center",
  },
  placeholderIcon: {
    fontSize: 64,
    marginBottom: 16,
  },
  cameraIcon: {
    width: 80,
    height: 80,
    marginBottom: 16,
  },
  placeholderText: {
    fontSize: 16,
    color: "#1379A2",
    textAlign: "center",
    fontWeight: "500",
  },
  inputGroup: {
    marginBottom: 20,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: "#333",
    marginBottom: 8,
    textAlign: "right",
  },
  input: {
    backgroundColor: "#F8F9FA",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E0E0E0",
    paddingVertical: 14,
    paddingHorizontal: 16,
    fontSize: 16,
    textAlign: "right",
  },
  barcodeInputRow: {
    flexDirection: "row",
    gap: 12,
  },
  scanButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#1379A2",
    borderRadius: 8,
    paddingVertical: 14,
    paddingHorizontal: 20,
    gap: 8,
  },
  scanButtonIcon: {
    fontSize: 18,
    color: "#FFFFFF",
  },
  scanIcon: {
    width: 20,
    height: 20,
    tintColor: "#FFFFFF",
  },
  scanButtonText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "600",
  },
  barcodeInput: {
    flex: 1,
    backgroundColor: "#F8F9FA",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E0E0E0",
    paddingVertical: 14,
    paddingHorizontal: 16,
    fontSize: 16,
    textAlign: "right",
  },
  barcodeInputActive: {
    borderColor: "#1379A2",
    borderWidth: 2,
  },
  selectInput: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    backgroundColor: "#F8F9FA",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E0E0E0",
    paddingVertical: 14,
    paddingHorizontal: 16,
    gap: 12,
  },
  selectText: {
    fontSize: 16,
    color: "#333",
  },
  selectIcon: {
    fontSize: 12,
    color: "#666",
  },
  dropdownMenu: {
    position: "absolute" as any,
    top: "100%" as any,
    left: 0,
    right: 0,
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E0E0E0",
    marginTop: 4,
    zIndex: 9999,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
  },
  dropdownItem: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#F0F0F0",
  },
  dropdownItemSelected: {
    backgroundColor: "#E8F4F8",
  },
  dropdownItemText: {
    fontSize: 14,
    color: "#333",
    textAlign: "right",
  },
  dropdownItemTextSelected: {
    color: "#1379A2",
    fontWeight: "600",
  },
  profitDisplay: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    backgroundColor: "#E8F8F0",
    borderRadius: 8,
    padding: 12,
    gap: 8,
    marginBottom: 16,
  },
  profitLabel: {
    fontSize: 14,
    color: "#333",
  },
  profitValue: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#2E7D32",
  },
  buttonContainer: {
    flexDirection: "row",
    justifyContent: "center",
    gap: 16,
    marginTop: 32,
    marginBottom: 40,
  },
  cancelButton: {
    backgroundColor: "#6B7280",
    borderRadius: 12,
    paddingVertical: 16,
    paddingHorizontal: 48,
  },
  cancelButtonText: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "600",
  },
  submitButton: {
    backgroundColor: "#1379A2",
    borderRadius: 12,
    paddingVertical: 16,
    paddingHorizontal: 48,
  },
  submitButtonText: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "600",
  },
  webDeleteButton: {
    backgroundColor: "#FEE2E2",
    borderRadius: 12,
    paddingVertical: 16,
    paddingHorizontal: 48,
    borderWidth: 1.5,
    borderColor: "#FCA5A5",
  },
  webDeleteButtonText: {
    color: "#DC2626",
    fontSize: 18,
    fontWeight: "600",
  },
  webModalOverlay: {
    position: "absolute" as any,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
  },
  webModalContent: {
    backgroundColor: "#FFFFFF",
    borderRadius: 20,
    padding: 32,
    width: 400,
    maxWidth: "90%" as any,
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.25,
    shadowRadius: 24,
    elevation: 20,
  },
  webModalIcon: {
    width: 72,
    height: 72,
    borderRadius: 36,
    backgroundColor: "#FEE2E2",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 16,
  },
  webModalTitle: {
    fontSize: 22,
    fontWeight: "700" as any,
    color: "#DC2626",
    marginBottom: 10,
  },
  webModalMessage: {
    fontSize: 16,
    color: "#666",
    textAlign: "center" as any,
    marginBottom: 8,
    lineHeight: 24,
  },
  webModalProductName: {
    fontSize: 18,
    fontWeight: "600" as any,
    color: "#333",
    marginBottom: 28,
  },
  webModalActions: {
    flexDirection: "row" as any,
    gap: 12,
    width: "100%",
  },
  webModalCancelBtn: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 12,
    backgroundColor: "#F3F4F6",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    alignItems: "center" as any,
  },
  webModalCancelText: {
    fontSize: 16,
    fontWeight: "600" as any,
    color: "#374151",
  },
  webModalDeleteBtn: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 12,
    backgroundColor: "#DC2626",
    alignItems: "center" as any,
  },
  webModalDeleteText: {
    fontSize: 16,
    fontWeight: "600" as any,
    color: "#FFFFFF",
  },
});

export default WebAddProductScreen;
